interface DataConfig {
  key: string;
  graph: string;
  transform: (data: any, fullData?: any) => any;
}

export const previewCallLevelSentimentsDataConfig: DataConfig[] = [
  {
    key: 'stress_detection',
    graph: 'SummaryCard',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'stress_detection');
      const stressData = metric?.data;
      return {
        title: 'STRESS DETECTION SUMMARY',
        subtitle: '(Multi-indicator)',
        value: stressData?.total_calls_with_stress?.toString() || '0',
        label: 'Stress Detected',
        breakdown: [
          { icon: '🎤', label: 'Audio', value: stressData?.by_modality?.find((item: any) => item.modality === 'audio')?.stress_count || 0 },
          { icon: '📹', label: 'Video', value: stressData?.by_modality?.find((item: any) => item.modality === 'video')?.stress_count || 0 },
          { icon: '📄', label: 'Text', value: stressData?.by_modality?.find((item: any) => item.modality === 'text')?.stress_count || 0 }
        ],
        link: 'View Stress Calls →'
      };
    }
  },
  {
    key: 'calls_requiring_review.critical',
    graph: 'MetricCard',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'calls_requiring_review');
      const critical = metric?.data?.categories?.find((c: any) => c.priority === 'critical');
      return {
        title: 'Critical',
        value: critical?.count?.toString() || '0',
        change: 'Immediate Action Required',
        changeType: 'negative',
        icon: 'SentimentSatisfiedIcon',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        accentColor: '#f093fb'
      };
    }
  },
  {
    key: 'calls_requiring_review.high',
    graph: 'MetricCard',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'calls_requiring_review');
      const high = metric?.data?.categories?.find((c: any) => c.priority === 'high');
      return {
        title: 'High',
        value: high?.count?.toString() || '0',
        change: 'Within 24h',
        changeType: 'warning',
        icon: 'SentimentDissatisfiedIcon',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        accentColor: '#fa709a'
      };
    }
  },
  {
    key: 'calls_requiring_review.medium',
    graph: 'MetricCard',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'calls_requiring_review');
      const medium = metric?.data?.categories?.find((c: any) => c.priority === 'medium');
      return {
        title: 'Medium',
        value: medium?.count?.toString() || '0',
        change: 'Within 1 week',
        changeType: 'neutral',
        icon: 'ReportProblemIcon',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        accentColor: '#4facfe'
      };
    }
  },
  {
    key: 'call_quality_distribution',
    graph: 'DonutChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'call_quality_distribution');
      return metric?.data?.segments?.map((item: any) => ({
        name: item.label,
        value: item.value,
        color: item.color
      })) || [];
    }
  },
  {
    key: 'call_type_breakdown',
    graph: 'BarChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'call_type_breakdown');
      return metric?.data?.types?.map((item: any) => ({
        name: item.display_name,
        value: item.total_calls,
        call_type: item.call_type || ''
      })) || [];
    }
  },
  {
    key: 'sentiment_trend',
    graph: 'StackedAreaChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'sentiment_trend');
      const sentimentData = metric?.data;
      let series = [];
      if (sentimentData?.series && sentimentData.series.length > 0) {
        series = sentimentData.series;
      }
      return { payload: series };
    }
  },
  {
    key: 'engagement_distribution',
    graph: 'Histogram',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'engagement_distribution');
      return metric?.data?.buckets?.map((item: any) => ({
        range: item.range_label,
        frequency: item.count
      })) || [];
    }
  },
  {
    key: 'emotion_patterns',
    graph: 'Histogram',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'emotion_patterns');
      return metric?.data?.emotions?.map((item: any) => ({
        type: item.display_name,
        frequency: item.count
      })) || [];
    }
  },
  {
    key: 'duration_quality',
    graph: 'ScatterChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'duration_quality');
      const scatterData = metric?.data;
      scatterData.tooltipDescription = scatterData?.tooltip_info?.what_it_measures || '';
      scatterData.tooltipPoints = scatterData?.tooltip_info?.suggested_actions || [];
      scatterData.quadrant_summaries = scatterData?.quadrants?.map((q: any) => ({
        quadrant: q.name.toLowerCase(),
        color: q.color,
        count: q.count,
        percentage: q.percentage
      })) || [];
      const points = (scatterData?.points || []).map((point: any) => ({
        call_id: point?.call_id || '',
        duration: point?.duration_minutes || 0,
        quality: point?.quality_score || 0,
        zone: point?.quadrant?.toLowerCase() || 'unknown',
        tooltip_text: point?.tooltip_text || '',
      }));
      console.log(points);
      return { payload: points, details: scatterData || {} };
    }
  },
  {
    key: 'talk_balance',
    graph: 'Histogram',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'talk_balance');
      const talkData = metric?.data;
      return talkData?.buckets?.map((item: any) => ({
        range: item.range_label,
        frequency: item.count
      })) || [];
    }
  },
  {
    key: 'period_comparison',
    graph: 'CompareInfoCards',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'period_comparison');
      const comparisonData = metric?.data;
      
      return comparisonData?.metrics?.map((item: any) => {
        const changeText = item.change_pct !== 0 
          ? `${item.change_pct > 0 ? '+' : ''}${item.change_pct.toFixed(1)}%`
          : 'No change';
        
        const statusText = item.trend === 'up' && item.is_improvement
          ? '✅ Improving'
          : item.trend === 'down' && !item.is_improvement
          ? '⚠️ Declining'
          : item.trend === 'stable'
          ? '➡️ Stable'
          : item.trend === 'up' && !item.is_improvement
          ? '⚠️ Increasing'
          : '➡️ Stable';

        return {
          cardTitle: item.name,
          score: item.current_value.toString(),
          lable: changeText,
          targetLine: `Previous: ${item.previous_value}`,
          targetSubLine: statusText,
          description: `Current Period: ${comparisonData?.current_period_label || ''}`
        };
      }) || [];
    }
  },
];